import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, CalendarClock, ChevronLeft, ChevronRight } from "lucide-react";
import { API, BACKEND, REVIEW_URL } from "@/utils";

const STATUSES = ["new", "contacted", "scheduled", "completed"];
const STATUS_COLORS = {
  new: "text-[#D4AF37] border-[#D4AF37]/50",
  contacted: "text-sky-400 border-sky-400/40",
  scheduled: "text-violet-400 border-violet-400/40",
  completed: "text-emerald-400 border-emerald-400/40",
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      localStorage.setItem("cr_token", data.access_token);
      onLogin(data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-5" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm border border-white/10 bg-[#111111] p-8" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-8">
          <img src="/assets/logo-icon.png" alt="ClearRoute" className="h-10 w-auto" />
          <div>
            <div className="font-display font-bold text-lg leading-none">
              <span className="text-[#D4AF37]">Clear</span>Route
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mt-1">Leads Panel</div>
          </div>
        </div>
        <input
          type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail"
          data-testid="admin-email-input"
          className="w-full bg-black/40 border border-white/10 px-4 py-3 text-sm mb-3 focus:outline-none focus:border-[#D4AF37] transition-colors"
        />
        <input
          type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
          data-testid="admin-password-input"
          className="w-full bg-black/40 border border-white/10 px-4 py-3 text-sm mb-6 focus:outline-none focus:border-[#D4AF37] transition-colors"
        />
        <button
          type="submit" disabled={loading}
          data-testid="admin-login-submit"
          className="w-full bg-[#D4AF37] hover:bg-[#E6C965] disabled:opacity-60 text-black font-bold py-3 text-sm transition-colors"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
};

const WeekView = ({ leads, onMove }) => {
  const [offset, setOffset] = useState(0);
  const [dragId, setDragId] = useState(null);
  const [overDay, setOverDay] = useState(null);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const days = useMemo(() => {
    const base = new Date();
    const dow = (base.getDay() + 6) % 7;
    base.setDate(base.getDate() - dow + offset * 7);
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(base);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [offset]);
  const todayIso = iso(new Date());
  const jobs = leads.filter((l) => l.status === "scheduled" && l.slot_date);
  const label = `${days[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${days[5].toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;

  const onDrop = (e, dayIso) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    const job = leads.find((l) => l.id === id);
    if (job && job.slot_date !== dayIso) onMove(job, dayIso);
    setDragId(null);
    setOverDay(null);
  };

  return (
    <div className="border border-white/10 mb-8" data-testid="week-view">
      <div className="flex items-center justify-between bg-[#151515] px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-white/40">
          <CalendarClock className="w-4 h-4 text-[#D4AF37]" /> Week · {label}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setOffset(offset - 1)} data-testid="week-prev" className="p-1.5 border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" aria-label="Previous week">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => setOffset(0)} data-testid="week-today" className="px-3 py-1.5 border border-white/10 text-[10px] uppercase tracking-widest hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">Now</button>
          <button onClick={() => setOffset(offset + 1)} data-testid="week-next" className="p-1.5 border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" aria-label="Next week">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6">
        {days.map((d) => {
          const dayIso = iso(d);
          const dayJobs = jobs.filter((j) => j.slot_date === dayIso).sort((a, b) => (a.slot_time || "").localeCompare(b.slot_time || ""));
          const isToday = dayIso === todayIso;
          const isOver = overDay === dayIso;
          return (
            <div
              key={dayIso}
              onDragOver={(e) => { e.preventDefault(); setOverDay(dayIso); }}
              onDragLeave={() => setOverDay(null)}
              onDrop={(e) => onDrop(e, dayIso)}
              className={`min-h-[120px] border-r border-white/5 last:border-r-0 p-3 transition-colors duration-200 ${isToday ? "bg-[#D4AF37]/5" : ""} ${isOver ? "bg-[#D4AF37]/15 ring-1 ring-inset ring-[#D4AF37]" : ""}`}
              data-testid={`week-day-${dayIso}`}
            >
              <div className={`text-[10px] uppercase tracking-widest ${isToday ? "text-[#D4AF37] font-bold" : "text-white/40"}`}>
                {d.toLocaleDateString("en-US", { weekday: "short" })}
              </div>
              <div className={`font-display font-bold text-xl ${isToday ? "text-[#D4AF37]" : "text-white/70"}`}>{d.getDate()}</div>
              <div className="mt-2 space-y-2">
                {dayJobs.length === 0 && <span className="text-white/15 text-xs">—</span>}
                {dayJobs.map((j) => (
                  <div
                    key={j.id}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData("text/plain", j.id); setDragId(j.id); }}
                    onDragEnd={() => { setDragId(null); setOverDay(null); }}
                    className={`border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-2 py-1.5 cursor-grab active:cursor-grabbing hover:border-[#D4AF37] transition-all duration-200 ${dragId === j.id ? "opacity-40 scale-95" : ""}`}
                    data-testid={`week-job-${j.id}`}
                    title="Drag to another day to reschedule"
                  >
                    <div className="text-[#D4AF37] text-[11px] font-bold">{j.slot_time}</div>
                    <div className="text-white/70 text-[11px] truncate">{j.name}</div>
                    <div className="text-white/35 text-[10px] capitalize truncate">{j.service}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem("cr_token")}` }), []);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/leads`, { headers: headers() }),
        axios.get(`${API}/leads/stats`, { headers: headers() }),
      ]);
      setLeads(l.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("cr_token");
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    const token = localStorage.getItem("cr_token");
    if (!token) {
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setUser(r.data);
        load();
      })
      .catch(() => {
        localStorage.removeItem("cr_token");
        setLoading(false);
      });
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { status }, { headers: headers() });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      if (status === "completed") {
        toast.success("Job completed — ask for the review!", {
          action: {
            label: "Copy review request",
            onClick: () =>
              navigator.clipboard.writeText(
                `Thanks for choosing ClearRoute Auto Glass! If you have 30 seconds, a Google review means the world to us: ${REVIEW_URL}`
              ),
          },
          duration: 8000,
        });
      } else {
        toast.success(`Lead → ${status}`);
      }
    } catch {
      toast.error("Update failed");
    }
  };

  const moveJob = async (job, newDate) => {
    try {
      const { data } = await axios.patch(`${API}/leads/${job.id}`, { slot_date: newDate }, { headers: headers() });
      setLeads((prev) =>
        prev.map((l) => (l.id === job.id ? { ...l, slot_date: data.slot_date, slot: data.slot, reminder_sent: false } : l))
      );
      toast.success(`Moved to ${data.slot}`);
    } catch {
      toast.error("Move failed");
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem("cr_token");
    setUser(null);
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-white/40 text-sm tracking-widest uppercase">Loading…</div>;
  }
  if (!user) return <Login onLogin={(u) => { setUser(u); setLoading(true); load(); }} />;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F8F8F8]" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-5 sm:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/assets/logo-icon.png" alt="ClearRoute" className="h-9 w-auto" />
          <div>
            <div className="font-display font-bold leading-none"><span className="text-[#D4AF37]">Clear</span>Route</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mt-0.5">Leads Panel</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="admin-refresh-button" className="p-2.5 border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" aria-label="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 px-4 py-2.5 border border-white/10 hover:border-red-400 hover:text-red-400 text-sm transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <main className="px-5 sm:px-8 py-8 max-w-[1400px] mx-auto">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="admin-stats">
            {["total", ...STATUSES].map((k) => (
              <div key={k} className="bg-[#151515] border border-white/5 p-5" data-testid={`stat-${k}`}>
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/40">{k}</div>
                <div className="font-display font-bold text-3xl mt-2 text-[#D4AF37]">{stats[k] ?? 0}</div>
              </div>
            ))}
          </div>
        )}

        <div className="overflow-x-auto border border-white/10">
          <WeekView leads={leads} onMove={moveJob} />
        </div>
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-sm min-w-[900px]" data-testid="leads-table">
            <thead>
              <tr className="bg-[#151515] text-left text-[10px] tracking-[0.2em] uppercase text-white/40">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Vehicle</th>
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3">Lang</th>
                <th className="px-4 py-3">Photo</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-white/30">No leads yet.</td></tr>
              )}
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-white/5 hover:bg-white/[0.03] transition-colors" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-4 text-white/50 whitespace-nowrap text-xs">
                    {new Date(l.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-semibold">{l.name}</div>
                    <a href={`tel:${l.phone}`} className="text-[#D4AF37] text-xs hover:underline">{l.phone}</a>
                    {l.slot && (
                      <div className="flex items-center gap-1.5 text-emerald-400/90 text-xs mt-1 font-semibold" data-testid={`lead-slot-${l.id}`}>
                        <CalendarClock className="w-3.5 h-3.5" /> {l.slot}
                      </div>
                    )}
                    {l.email && <div className="text-white/40 text-xs">{l.email}</div>}
                    {l.message && <div className="text-white/40 text-xs mt-1 max-w-[220px] truncate" title={l.message}>{l.message}</div>}
                  </td>
                  <td className="px-4 py-4 text-white/60 text-xs">
                    <div>{l.vehicle || "—"}</div>
                    <div className="text-white/35 font-mono mt-0.5">{l.plate_vin || ""}</div>
                  </td>
                  <td className="px-4 py-4 text-white/60 text-xs capitalize">{l.service || "—"}{l.insurance ? <div className="text-[#D4AF37]/80 text-[11px] mt-0.5 normal-case">{l.insurance}</div> : null}</td>
                  <td className="px-4 py-4 text-white/60 text-xs uppercase">{l.language}</td>
                  <td className="px-4 py-4">
                    {l.photo_url ? (
                      <a href={`${BACKEND}${l.photo_url}`} target="_blank" rel="noopener noreferrer" data-testid={`lead-photo-${l.id}`}>
                        <img src={`${BACKEND}${l.photo_url}`} alt="damage" className="w-14 h-14 object-cover border border-white/10 hover:border-[#D4AF37] transition-colors" />
                      </a>
                    ) : (
                      <span className="text-white/25 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <select
                      value={l.status}
                      onChange={(e) => setStatus(l.id, e.target.value)}
                      data-testid={`lead-status-${l.id}`}
                      className={`bg-transparent border px-2.5 py-1.5 text-xs font-bold uppercase tracking-wider focus:outline-none ${STATUS_COLORS[l.status]}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s} className="bg-[#151515] text-white">{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
